import { useRef, useState } from 'react';
import { failureOf, said } from '@/api/errors';
import { useAttachments, useDriveAttachment, useDriveFiles, useDriveFolders, useUploadAttachment } from '@/api/hooks';
import { Button, Kicker } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { useMeOrThrow } from '@/app/MeContext';
import { Select } from '@/ui/fields';

/** The same ceiling the platform holds, said before the bytes are sent. */
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.csv,.txt,.docx,.xlsx,.pptx';

function base64Of(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('the file could not be read'));
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.readAsDataURL(file);
  });
}

/**
 * Files that go with a draft (ADR-0114). A file never rides inside the
 * message: its link is written into the body above, where you read it
 * before you send — the company's Drive link for a Drive file, or the
 * platform's own link, good for thirty days, for one you upload.
 */
export function Attachments({ draftId }: { draftId: string }) {
  const q = useAttachments(draftId);
  const upload = useUploadAttachment(draftId);
  const fromDrive = useDriveAttachment(draftId);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState(false);
  const files = q.data?.attachments ?? [];

  const choose = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast({ text: 'A file is at most 5 MB; share a larger one from Drive.', tone: 'danger' });
      return;
    }
    upload.mutate(
      { filename: file.name, mediaType: file.type || 'application/octet-stream', dataBase64: await base64Of(file) },
      {
        onSuccess: (r) => toast({ text: `${r.attachment.filename} is linked in the draft. The link works for thirty days.` }),
        onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
      },
    );
    if (input.current) input.current.value = '';
  };

  return (
    <div className="flex flex-col gap-2" data-testid="attachments">
      <div className="flex flex-wrap items-center gap-2">
        <Kicker>Files</Kicker>
        {files.map((f) => (
          <span key={f.id} className="rounded-brand border border-line px-2 py-0.5 text-dense" data-testid="attachment">
            {f.filename}
            <span className="text-meta"> · {f.source === 'drive' ? 'Drive' : 'link for 30 days'}</span>
          </span>
        ))}
        <input ref={input} type="file" accept={ACCEPT} className="hidden" aria-label="Upload a file" onChange={(e) => void choose(e.target.files?.[0])} />
        <Button size="row" onClick={() => input.current?.click()} disabled={upload.isPending}>
          Upload a file
        </Button>
        <Button size="row" onClick={() => setPicking((p) => !p)}>
          From Drive
        </Button>
      </div>
      {picking && (
        <DrivePicker
          onPick={(key, fileId) =>
            fromDrive.mutate(
              { key, fileId },
              {
                onSuccess: (r) => {
                  setPicking(false);
                  toast({ text: `${r.attachment.filename} is linked in the draft. It opens for whom Drive allows.` });
                },
                onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
              },
            )
          }
          busy={fromDrive.isPending}
        />
      )}
      {(upload.isError || fromDrive.isError) && <Refusal compact failure={failureOf(upload.error ?? fromDrive.error)} />}
    </div>
  );
}

/** The department's own folders, and a folder's files as Drive lists them now, under your consent. */
function DrivePicker({ onPick, busy }: { onPick: (key: string, fileId: string) => void; busy: boolean }) {
  const me = useMeOrThrow();
  const folders = useDriveFolders();
  const [key, setKey] = useState('');
  const files = useDriveFiles(key, undefined, key !== '');
  const plain = (folders.data?.folders ?? []).filter((f) => !f.onePer && f.department === me.department);
  return (
    <div className="flex flex-col gap-2 rounded-brand border border-line p-3" data-testid="drive-picker">
      <Select
        aria-label="Drive folder"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        className="max-w-[420px]"
      >
        <option value="">A folder of this department’s…</option>
        {plain.map((f) => (
          <option key={f.key} value={f.key}>
            {f.path}
          </option>
        ))}
      </Select>
      {files.isError && <Refusal compact failure={failureOf(files.error)} />}
      {files.data && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {files.data.files.filter((f) => !f.isFolder).map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-dense">
              <span className="flex-1">{f.name}</span>
              <Button size="row" disabled={busy} onClick={() => onPick(key, f.id)}>
                Link it
              </Button>
            </li>
          ))}
          {files.data.files.filter((f) => !f.isFolder).length === 0 && <li className="text-dense text-meta">No files in this folder.</li>}
        </ul>
      )}
    </div>
  );
}
